/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, type ReactNode, useEffect, useState } from 'react';

import AnalyticsWebClient, { originType } from '@atlassiansox/analytics-web-client';
import { cssMap, jsx } from '@compiled/react';

import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { getAnalyticsEnv } from '../utils';

type Props = {
	icon: ReactNode;
	iframeUrl: string;
	name: string;
	panelType?: string;
	parentProduct?: string;
	product: string;
	url: string;
};

const styles = cssMap({
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
	},
	body: {
		display: 'flex',
		flexGrow: 1,
		overflowY: 'auto',
	},
});

export const IFramePreviewPanel = memo(
	({ url, iframeUrl, name, icon, panelType, product, parentProduct }: Props) => {
		const { close } = usePreviewPanelsActions();
		const [analyticsClient, setAnalyticsClient] = useState<AnalyticsWebClient>();

		useEffect(() => {
			setAnalyticsClient(
				new AnalyticsWebClient({
					product,
					env: getAnalyticsEnv(window.location.hostname),
					origin: originType.WEB,
				}),
			);
		}, [product]);

		return (
			<PreviewPanel>
				<PreviewPanelHeader
					onClose={close}
					url={url}
					parentProduct={parentProduct}
					panelType={panelType}
					analyticsClient={analyticsClient}
				>
					<PreviewPanelTitle title={name} icon={icon} />
				</PreviewPanelHeader>
				<PreviewPanelBody
					styles={styles.body}
					parentProduct={parentProduct}
					panelType={panelType}
					analyticsClient={analyticsClient}
				>
					<iframe src={iframeUrl} css={styles.iframe} title={name} />
				</PreviewPanelBody>
			</PreviewPanel>
		);
	},
);
